import { FC } from "react";
import dynamic from "next/dynamic";
import parse, {
  DOMNode,
  domToReact,
  HTMLReactParserOptions,
} from "html-react-parser";

import { CookieName } from "@/utils/cookie.helper";

// Serialise a parsed htmlparser2 DOM node back to an HTML string.
// We need this for SVG content because html-react-parser/React lowercases
// all attribute names, but SVG attributes like `viewBox` are case-sensitive
// in the SVG DOM. Once dangerouslySetInnerHTML hands the string to the
// browser, the browser parses it as HTML5 and applies the correct
// case-aware SVG attribute semantics.
function nodeToHtml(node: DOMNode): string {
  // text
  if (node.type === "text") return (node as unknown as { data: string }).data;
  // tag / script / style
  if ("name" in node && (node.type === "tag" || node.type === "script" || node.type === "style")) {
    const el = node as unknown as {
      name: string;
      attribs?: Record<string, string>;
      children?: DOMNode[];
    };
    const attrs = el.attribs
      ? Object.entries(el.attribs)
          .map(([k, v]) => `${k}="${String(v).replace(/"/g, "&quot;")}"`)
          .join(" ")
      : "";
    const children = (el.children ?? []).map((c) => nodeToHtml(c)).join("");
    return `<${el.name}${attrs ? " " + attrs : ""}>${children}</${el.name}>`;
  }
  return "";
}

// ----------------------------------------------------------------------------

const CookieBannerWrapper = dynamic(
  () => import("@/components/Cookies/CookieBannerWrapper"),
  {
    ssr: false,
  },
);

// ----------------------------------------------------------------------------

export type CKEditorHtmlContentProps = {
  htmlContent: string;
};

const CKEditorHtmlContent: FC<CKEditorHtmlContentProps> = ({ htmlContent }) => {
  const options: HTMLReactParserOptions = {
    replace: (domNode) => {
      // SVG infographics: html-react-parser/React lowercases attribute names,
      // which breaks SVG (viewBox -> viewbox). Bypass React's attribute
      // handling for the whole <figure class="svg-figure"> subtree by feeding
      // the raw HTML to the browser via dangerouslySetInnerHTML.
      if (
        domNode.type === "tag" &&
        domNode.name === "figure" &&
        typeof (domNode as unknown as { attribs?: Record<string, string> }).attribs?.class === "string" &&
        (domNode as unknown as { attribs: { class: string } }).attribs.class
          .split(/\s+/)
          .includes("svg-figure")
      ) {
        const raw = nodeToHtml(domNode);
        return (
          <div
            className="my-6"
            dangerouslySetInnerHTML={{ __html: raw }}
          />
        );
      }

      if (domNode.type === "tag" && domNode.name === "iframe") {
        const { attribs } = domNode;

        // If youtube video, check for cookies
        if (attribs.src.includes("www.youtube.com")) {
          return (
            <CookieBannerWrapper category={CookieName.FUNCTIONAL}>
              <iframe className="aspect-video w-full py-4" src={attribs.src} />
            </CookieBannerWrapper>
          );
        }

        return (
          <iframe className="aspect-video w-full py-4" src={attribs.src} />
        );
      }
      if (domNode.type === "tag" && domNode.name === "video") {
        const { attribs, children } = domNode;
        return (
          <figure className="py-4">
            <video
              controls
              className="aspect-video w-full bg-black-950"
              {...attribs}
            >
              {domToReact(children as DOMNode[])}
            </video>
          </figure>
        );
      }
    },
  };

  return <>{parse(htmlContent, options)}</>;
};

export default CKEditorHtmlContent;
